import React, { useState, useEffect, useRef } from "react";
import Header from "../Header";
import gsap from "gsap";
import axios from "axios";
import TweetPromote from "../TweetPromote";
import { toast } from 'react-toastify';

const CountDownAnimationModal = ({
  isOpen,
  onClose,
  electedUser,
  user,
  isSoundOn,
  volume,
  setVolume,
  marketerData,
}) => {
  const [fadeUsername, setFadeUsername] = useState(false);
  const [fadeZetharName, setFadeZetharName] = useState(false);
  const [winnerName, setWinnerName] = useState(false);
  const [loserName, setLoserName] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isChatting, setIsChatting] = useState(false);
  const [showShutterImage, setShowShutterImage] = useState(false);

  const videoRefs = useRef([
    React.createRef(),
    React.createRef(),
    React.createRef(),
    React.createRef(),
  ]);

  const [audio_countdown] = useState(new Audio("sound/countdown-1.mp3"));
  const [audio_winner] = useState(new Audio("sound/win_scenario.mp3"));
  const [audio_loser] = useState(new Audio("sound/lose_scenario.mp3"));

  const saveWinner = async (winner) => {
    try {
        const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const adminToken = localStorage.getItem('admin_token');

        const giveawayData = {
            user_id: null,
            reward_id: null,
            marketor_id: marketerData.id,
            twitch_id: marketerData.refid,
            refid: winner.user_login,
            status: 'winner',
        };

        const response = await axios.post(`${backendUrl}/api/all/giveaway_attempts`, giveawayData, {
            headers: { Authorization: `Bearer ${adminToken}` },
        });

        if (response.status === 201) {
            toast.success(`Winner ${winner.user_name} recorded! They can register to claim their prize.`);
        } else {
            toast.error('Failed to record the winner.');
        }
    } catch (error) {
        console.error('Error recording winner:', error);
        toast.error('An error occurred while recording the winner.');
    }
  };

  const saveLoser = async (loser) => {
    try {
        const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const adminToken = localStorage.getItem('admin_token');

        const giveawayData = {
            user_id: null,
            reward_id: null,
            marketor_id: marketerData.id,
            twitch_id: marketerData.refid,
            refid: loser.user_login,
            status: 'loser',
        };

        const response = await axios.post(`${backendUrl}/api/all/giveaway_attempts`, giveawayData, {
            headers: { Authorization: `Bearer ${adminToken}` },
        });

        if (response.status === 201) {
            toast.info(`Loser ${loser.user_name} recorded.`);
        } else {
            toast.error('Failed to record the loser.');
        }
    } catch (error) {
        console.error('Error recording loser:', error);
        toast.error('An error occurred while recording the loser.');
    }
  };

  // Reset isChatting when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsChatting(false);
    }
  }, [isOpen]);

  useEffect(() => {
    currentVideoIndex === 0 && audio_countdown.play();
    if (isSoundOn) {
      audio_countdown.volume = volume / 100;
    } else {
      audio_countdown.volume = 0;
    }
  }, [isSoundOn, volume]);

  useEffect(() => {
    currentVideoIndex === 1 && audio_winner.play();
    if (isSoundOn) {
      audio_winner.volume = volume / 100;
    } else {
      audio_winner.volume = 0;
    }
  }, [isSoundOn, volume, currentVideoIndex]);

  useEffect(() => {
    currentVideoIndex === 2 && audio_loser.play();
    if (isSoundOn) {
      audio_loser.volume = volume / 100;
    } else {
      audio_loser.volume = 0;
    }
  }, [isSoundOn, volume, currentVideoIndex]);

  useEffect(() => {
    const handleVideoEnded = () => {
      if (currentVideoIndex === 0) {
        setShowShutterImage(true);
        setFadeUsername(false);
        setFadeZetharName(false);
      } else if (currentVideoIndex === 1) {
        setCurrentVideoIndex(3);
        setWinnerName(false);
      } else if (currentVideoIndex === 2) {
        setShowShutterImage(true);
        setLoserName(false);
      } else if (currentVideoIndex === 3) {
        onClose();
      }
    };

    const videoRef = videoRefs.current[currentVideoIndex].current;
    if (videoRef) {
      videoRef.addEventListener("ended", handleVideoEnded);
      videoRef.play();
    }

    return () => {
      if (videoRef) {
        videoRef.removeEventListener("ended", handleVideoEnded);
      }
    };
  }, [currentVideoIndex, onClose]);

  useEffect(() => {
    setTimeout(() => {
      setFadeUsername(true);
      setFadeZetharName(true);
    }, 500);
  }, []);

  // --- Stream Live Check and Console Log ---
  useEffect(() => {
    if (!isOpen || !user?.accessToken || !user?.id) return;

    const fetchStreamInfo = async () => {
      try {
        const url = `https://api.twitch.tv/helix/streams?user_id=${user.id}`;
        const res = await axios.get(url, {
          headers: {
            'Authorization': `Bearer ${user.accessToken}`,
            'Client-ID': process.env.REACT_APP_TWITCH_CLIENT_ID,
          },
        });
        const streamInfo = res.data.data && res.data.data.length > 0 ? res.data.data[0] : null;
        console.log('Stream is', streamInfo ? 'LIVE' : 'OFFLINE');
      } catch (error) {
        console.error('Error fetching stream info:', error);
      }
    };

    fetchStreamInfo();
  }, [isOpen, user?.accessToken, user?.id]);

  // --- Live Chat Detection via Twitch IRC WebSocket ---
  useEffect(() => {
    if (!isOpen || !user?.accessToken || !user?.name || !electedUser?.user_name) return;

    const channelName = user.name.toLowerCase();
    const targetUser = (electedUser.user_name || '').toLowerCase().trim();
    const oauthToken = user.accessToken.startsWith("oauth:") ? user.accessToken : `oauth:${user.accessToken}`;
    let ws = new window.WebSocket("wss://irc-ws.chat.twitch.tv:443");
    let detected = false;

    ws.addEventListener("open", () => {
      ws.send(`PASS ${oauthToken}`);
      ws.send(`NICK ${user.name}`);
      ws.send(`JOIN #${channelName}`);
      ws.send("CAP REQ :twitch.tv/membership twitch.tv/tags twitch.tv/commands");
      console.log("Connected to Twitch IRC WebSocket");
    });

    ws.addEventListener("message", (event) => {
      const msg = event.data;
      console.log("RAW:", msg);

      if (msg.startsWith("PING")) {
        ws.send("PONG :tmi.twitch.tv");
        return;
      }

      // More robust regex and logging
      if (/PRIVMSG/.test(msg)) {
        const match = msg.match(/:([^!]+)!.* PRIVMSG #[^ ]+ :(.*)/);
        if (match) {
          const username = (match[1] || '').toLowerCase().trim();
          const content = match[2];
          // Defensive: always ensure string and lowercase/trim for both
          console.log('Comparing:', username, targetUser, username === targetUser);
          if (username === targetUser && !detected) {
            console.log(`>>> [${username}] says: ${content}`);
            setIsChatting(true);
            detected = true;
            ws.close();
          }
        }
      }
    });

    ws.addEventListener("close", () => {
      console.log("Disconnected from IRC");
    });
    ws.addEventListener("error", (err) => {
      console.error("WebSocket error:", err);
    });

    return () => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [isOpen, user?.accessToken, user?.name, electedUser?.user_name]);

  // WINNER: Trigger winner animation as soon as isChatting becomes true
  useEffect(() => {
    if (isChatting) {
      saveWinner(electedUser);
      setWinnerName(true);
      setFadeUsername(false);
      setFadeZetharName(false);
      setCurrentVideoIndex(1);
    }
  }, [isChatting]);

  // LOSER: Only trigger loser animation if isChatting is still false after 33s
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      if (!isChatting) {
        saveLoser(electedUser);
        setCurrentVideoIndex(2);
        setLoserName(true);
        setFadeUsername(false);
        setFadeZetharName(false);
      }
    }, 33000);
    return () => clearTimeout(timer);
  }, [isOpen, isChatting]);

  useEffect(() => {
    const shutterImage = document.querySelector(".shutter-image");

    if (shutterImage) {
      const hideShutter = () => {
        setShowShutterImage(false);
        setCurrentVideoIndex(3);

        if (isSoundOn) {
          gsap.to(audio_loser, {
            volume: 0,
            duration: 1,
            onComplete: () => {
              audio_loser.pause();
            },
          });
        }
      };

      shutterImage.addEventListener("animationend", hideShutter);

      return () => {
        shutterImage.removeEventListener("animationend", hideShutter);
      };
    }
  }, [showShutterImage]);

  const modalStyle = {
    opacity: isOpen ? 1 : 0,
    transition: "opacity 0.3s ease-in-out, transform 0.3s ease-in-out 0.2s",
    position: "fixed",
    top: "0%",
    left: "0%",
    zIndex: 900,
  };

  const fadeAnimationStyle = {
    transition: "opacity 0s ease-in-out",
    opacity: fadeUsername && fadeZetharName ? 1 : 0,
  };

  const winnerNameAnimationStyle = {
    opacity: winnerName ? 1 : 0,
    transition: "opacity 0s ease-in-out, transform 0.5s ease-in-out",
    transform: `translateX(${winnerName ? 0 : "-100%"})`,
  };

  const loserNameAnimationStyle = {
    opacity: loserName ? 1 : 0,
    transition: "opacity 0s ease-in-out, transform 0.5s ease-in-out",
    transform: `translateX(${winnerName ? "100%" : 0})`,
  };

  // Debug: log what will be passed to TweetPromote
  if (winnerName) {
    console.log("TweetPromote props:", user?.name, electedUser?.user_name);
  }

  return (
    <>
      <div className="countdown-modal-back" style={modalStyle}>
        <Header />
        {showShutterImage && <div className="shutter-image"></div>}
        <video
          ref={videoRefs.current[0]}
          src="/images/countdown_video.mp4"
          muted
          className={`countdown-start`}
          style={{ display: currentVideoIndex === 0 ? "block" : "none" }}
        />
        <video
          ref={videoRefs.current[1]}
          src="/images/win_scenario.mp4"
          muted
          className={`countdown-start`}
          style={{ display: currentVideoIndex === 1 ? "block" : "none" }}
        />
        <video
          ref={videoRefs.current[2]}
          src="/images/lose_scenario.mp4"
          muted
          className={`countdown-start`}
          style={{ display: currentVideoIndex === 2 ? "block" : "none" }}
        />
        <video
          ref={videoRefs.current[3]}
          src="/images/transition_main_menu.mp4"
          muted
          className={`countdown-start`}
          style={{ display: currentVideoIndex === 3 ? "block" : "none" }}
        />

        <div className="elected-username" style={fadeAnimationStyle}>
          {`${electedUser ? electedUser.user_name : "viewer_name"}`}
        </div>
        <div className="zethar-name" style={fadeAnimationStyle}>Zethar</div>

        <div className="winner-title" style={winnerNameAnimationStyle}>
          {`${electedUser ? electedUser.user_name : "viewer_name"} is the winner!`}
        </div>

        <div className="loser-title" style={loserNameAnimationStyle}>
          {`${electedUser ? electedUser.user_name : "viewer_name"} lost the battle!`}
        </div>

        {/* Only show TweetPromote if there is a winner */}
        {winnerName && (
          <TweetPromote
            isSoundOn={isSoundOn}
            volume={volume}
            setVolume={setVolume}
            streamerUsername={user?.name}
            winnerUsername={electedUser?.user_name}
          />
        )}

        <div className="animation-board"></div>
      </div>
    </>
  );
};

export default CountDownAnimationModal;
